// src/components/vehicles/VehicleDashboard.js
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { api, formatDate, daysLeftText, getDeadlineColor, getDeadlineLabel } from "../../utils/api";
import { AuthContext } from "../../context/AuthContext";
import "../../css/Fleet.css";

const COLORS = ["#3b82f6", "#ef4444", "#f59e0b", "#10b981", "#8b5cf6", "#ec4899", "#06b6d4", "#f97316", "#84cc16", "#6366f1"];

const VehicleDashboard = () => {
  const [summary, setSummary] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [horizonDays, setHorizonDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === "Administrator";

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, upcomingRes] = await Promise.all([
        api.get("/vehicle-dashboard"),
        api.get("/services/upcoming"),
      ]);
      setSummary(summaryRes.data);
      setUpcoming(upcomingRes.data.data || []);
      if (upcomingRes.data.horizonDays) setHorizonDays(upcomingRes.data.horizonDays);
    } catch (err) {
      console.error("Błąd ładowania dashboard:", err);
    }
    setLoading(false);
  };

  if (loading) {
    return <div className="fleet-loading"><ProgressSpinner /></div>;
  }

  const formatCurrency = (val) => {
    if (!val) return "0 zł";
    return new Intl.NumberFormat("pl-PL", { style: "currency", currency: "PLN" }).format(val);
  };

  const formatCurrencyShort = (val) => {
    if (!val) return "0";
    if (val >= 1000) return `${(val / 1000).toFixed(1)}k`;
    return val.toFixed(0);
  };

  // Porównanie z poprzednim miesiącem
  const getChangePercent = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const costChange = summary ? getChangePercent(summary.repairsCostThisMonth, summary.repairsCostLastMonth) : 0;
  const countChange = summary ? getChangePercent(summary.repairsThisMonth, summary.repairsLastMonth) : 0;

  // Dane do doughnut serwisów
  const serviceDonut = summary ? [
    { name: "OK", value: summary.ok || 0, color: "#10b981" },
    { name: "Ostrzeżenie", value: summary.warning || 0, color: "#f59e0b" },
    { name: "Przeterminowane", value: summary.overdue || 0, color: "#ef4444" },
  ].filter(d => d.value > 0) : [];

  const statusBodyTemplate = (rowData) => {
    const label = getDeadlineLabel(rowData.deadlineStatus);
    const cls =
      rowData.deadlineStatus === "overdue" ? "fleet-tag--danger" :
      rowData.deadlineStatus === "warning" ? "fleet-tag--warning" : "fleet-tag--success";
    return <span className={`fleet-tag ${cls}`}>{label}</span>;
  };

  const vehicleBodyTemplate = (rowData) => (
    <span className="fleet-vehicle-link" onClick={() => navigate(`/pojazdy/${rowData.vehicle_id}`)}>
      <i className="pi pi-car" style={{ fontSize: "0.8rem" }} />
      {rowData.brand} {rowData.model} ({rowData.registration_number})
    </span>
  );

  const daysBodyTemplate = (rowData) => {
    const color = getDeadlineColor(rowData.deadlineStatus);
    return <span style={{ color, fontWeight: 600 }}>{daysLeftText(rowData.daysLeft)}</span>;
  };

  const customTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="fleet-tooltip">
          <p style={{ margin: "0 0 0.25rem", fontWeight: 600, fontSize: "0.85rem" }}>{label}</p>
          {payload.map((p, i) => (
            <p key={i} style={{ margin: 0, color: p.color, fontSize: "0.8rem" }}>
              {p.name}: {p.name === "Koszt" ? formatCurrency(p.value) : p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const overdueItems = upcoming.filter(u => u.deadlineStatus === "overdue");
  const warningItems = upcoming.filter(u => u.deadlineStatus === "warning");

  return (
    <div className="fleet-page fleet-animate-in">
      {/* Header */}
      <div className="fleet-page-header">
        <div>
          <h1><span className="fleet-header-icon">🚛</span>Panel floty</h1>
          <p className="fleet-page-subtitle">Przegląd stanu pojazdów i nadchodzących terminów</p>
        </div>
      </div>

      {/* Alerty — przeterminowane serwisy (na samej górze) */}
      {overdueItems.length > 0 && (
        <div className="fleet-alert fleet-alert--danger">
          <div className="fleet-alert-icon"><i className="pi pi-exclamation-circle" /></div>
          <div className="fleet-alert-body">
            <strong>Przeterminowane serwisy ({overdueItems.length})</strong>
            <div className="fleet-alert-list">
              {overdueItems.slice(0, 5).map((item, i) => (
                <span key={i} className="fleet-alert-item" onClick={() => navigate(`/pojazdy/${item.vehicle_id}`)}>
                  {item.registration_number} — {item.serviceTypeName} (ważne do: {formatDate(item.valid_until)}, {daysLeftText(item.daysLeft)})
                </span>
              ))}
              {overdueItems.length > 5 && <span className="fleet-alert-more">...i {overdueItems.length - 5} więcej</span>}
            </div>
          </div>
        </div>
      )}

      {warningItems.length > 0 && (
        <div className="fleet-alert fleet-alert--warning">
          <div className="fleet-alert-icon"><i className="pi pi-info-circle" /></div>
          <div className="fleet-alert-body">
            <strong>Zbliżające się terminy ({warningItems.length})</strong>
            <div className="fleet-alert-list">
              {warningItems.slice(0, 5).map((item, i) => (
                <span key={i} className="fleet-alert-item" onClick={() => navigate(`/pojazdy/${item.vehicle_id}`)}>
                  {item.registration_number} — {item.serviceTypeName} (ważne do: {formatDate(item.valid_until)}, {daysLeftText(item.daysLeft)})
                </span>
              ))}
              {warningItems.length > 5 && <span className="fleet-alert-more">...i {warningItems.length - 5} więcej</span>}
            </div>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="fleet-stats-grid fleet-stats-grid--6">
        <div className="fleet-stat-card fleet-stat-card--primary" onClick={() => navigate("/pojazdy/lista")} style={{ cursor: "pointer" }}>
          <div className="fleet-stat-icon fleet-stat-icon--primary"><i className="pi pi-car" /></div>
          <div className="fleet-stat-value fleet-stat-value--primary">{summary?.activeVehicles || 0}</div>
          <div className="fleet-stat-label">Aktywne pojazdy</div>
          {summary?.inactiveVehicles > 0 && (
            <div className="fleet-stat-sub">+ {summary.inactiveVehicles} nieaktywnych</div>
          )}
        </div>

        <div className="fleet-stat-card fleet-stat-card--success">
          <div className="fleet-stat-icon fleet-stat-icon--success"><i className="pi pi-building" /></div>
          <div className="fleet-stat-value fleet-stat-value--success">{summary?.companyCount || 0}</div>
          <div className="fleet-stat-label">Firm z pojazdami</div>
        </div>

        <div className="fleet-stat-card fleet-stat-card--danger">
          <div className="fleet-stat-icon fleet-stat-icon--danger"><i className="pi pi-exclamation-triangle" /></div>
          <div className="fleet-stat-value fleet-stat-value--danger">{summary?.overdue || 0}</div>
          <div className="fleet-stat-label">Przeterminowane serwisy</div>
        </div>

        <div className="fleet-stat-card fleet-stat-card--warning">
          <div className="fleet-stat-icon fleet-stat-icon--warning"><i className="pi pi-clock" /></div>
          <div className="fleet-stat-value fleet-stat-value--warning">{summary?.warning || 0}</div>
          <div className="fleet-stat-label">Serwisy do sprawdzenia</div>
        </div>

        <div className="fleet-stat-card fleet-stat-card--purple">
          <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-wrench" /></div>
          <div className="fleet-stat-value fleet-stat-value--purple">{summary?.repairsThisMonth || 0}</div>
          <div className="fleet-stat-label">Napraw w tym miesiącu</div>
          <div className={`fleet-stat-change ${countChange > 0 ? "fleet-stat-change--up" : countChange < 0 ? "fleet-stat-change--down" : ""}`}>
            {countChange > 0 ? "↑" : countChange < 0 ? "↓" : "—"} {Math.abs(countChange)}% vs poprz. m-c
          </div>
        </div>

        {isAdmin && <div className="fleet-stat-card fleet-stat-card--purple">
          <div className="fleet-stat-icon fleet-stat-icon--purple"><i className="pi pi-wallet" /></div>
          <div className="fleet-stat-value fleet-stat-value--purple" style={{ fontSize: "1.35rem" }}>
            {formatCurrency(summary?.repairsCostThisMonth)}
          </div>
          <div className="fleet-stat-label">Koszt napraw (miesiąc)</div>
          <div className={`fleet-stat-change ${costChange > 0 ? "fleet-stat-change--up" : costChange < 0 ? "fleet-stat-change--down" : ""}`}>
            {costChange > 0 ? "↑" : costChange < 0 ? "↓" : "—"} {Math.abs(costChange)}% vs poprz. m-c
          </div>
        </div>}
      </div>

      {/* Upcoming services table */}
      <div className="fleet-card">
        <div className="fleet-card-header">
          <h3 className="fleet-card-title"><i className="pi pi-calendar" /> Nadchodzące terminy serwisów <span style={{ fontWeight: 400, fontSize: "0.8rem", color: "var(--fleet-text-muted)" }}>(Najbliższe {horizonDays} dni)</span></h3>
        </div>
        <div className="fleet-card-body--flush fleet-table">
          <DataTable
            value={upcoming}
            paginator
            rows={10}
            emptyMessage="Brak nadchodzących terminów"
            sortField="daysLeft"
            sortOrder={1}
            responsiveLayout="stack"
            breakpoint="600px"
          >
            <Column header="Pojazd" body={vehicleBodyTemplate} />
            <Column field="serviceTypeName" header="Typ serwisu" sortable />
            <Column header="Ważne do" body={(row) => formatDate(row.valid_until)} sortable sortField="valid_until" />
            <Column header="Pozostało" body={daysBodyTemplate} sortable sortField="daysLeft" />
            <Column header="Status" body={statusBodyTemplate} sortable sortField="deadlineStatus" />
          </DataTable>
        </div>
      </div>

      {/* Charts Row */}
      <div className="fleet-dashboard-grid">
        {/* Koszty napraw — trend 6 miesięcy */}
        {isAdmin && <div className="fleet-chart-card">
          <div className="fleet-chart-title"><i className="pi pi-chart-line" /> Koszty napraw (6 miesięcy)</div>
          {summary?.repairsTrend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={summary.repairsTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "var(--fleet-text-secondary)" }} />
                <YAxis tickFormatter={(v) => `${formatCurrencyShort(v)}`} tick={{ fontSize: 11, fill: "var(--fleet-text-muted)" }} />
                <Tooltip content={customTooltip} />
                <Line type="monotone" dataKey="totalCost" name="Koszt" stroke="var(--fleet-primary)" strokeWidth={2.5} dot={{ r: 4, fill: "var(--fleet-primary)" }} activeDot={{ r: 6 }} />
                <Line type="monotone" dataKey="count" name="Ilość" stroke="var(--fleet-success)" strokeWidth={2} dot={{ r: 3, fill: "var(--fleet-success)" }} />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="fleet-chart-empty"><i className="pi pi-chart-line" style={{ fontSize: "2rem", opacity: 0.3 }} /><p>Brak danych</p></div>
          )}
        </div>}

        {/* Status serwisów — doughnut */}
        <div className="fleet-chart-card">
          <div className="fleet-chart-title"><i className="pi pi-shield" /> Status serwisów</div>
          {serviceDonut.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={serviceDonut} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`} labelLine={{ stroke: "var(--fleet-text-muted)" }}>
                  {serviceDonut.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: "0.75rem" }} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="fleet-chart-empty"><i className="pi pi-shield" style={{ fontSize: "2rem", opacity: 0.3 }} /><p>Brak serwisów</p></div>
          )}
        </div>

        {/* Pojazdy wg typu — bar chart */}
        <div className="fleet-chart-card">
          <div className="fleet-chart-title"><i className="pi pi-tags" /> Pojazdy wg typu</div>
          {summary?.vehiclesByType?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.vehiclesByType}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--fleet-text-secondary)" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--fleet-text-muted)" }} />
                <Tooltip />
                <Bar dataKey="count" name="Ilość" fill="var(--fleet-primary)" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="fleet-chart-empty"><i className="pi pi-tags" style={{ fontSize: "2rem", opacity: 0.3 }} /><p>Brak danych</p></div>
          )}
        </div>

        {/* Pojazdy wg firmy — bar chart */}
        <div className="fleet-chart-card">
          <div className="fleet-chart-title"><i className="pi pi-building" /> Pojazdy wg firmy</div>
          {summary?.vehiclesByCompany?.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={summary.vehiclesByCompany} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--fleet-border-light)" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "var(--fleet-text-muted)" }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: "var(--fleet-text-secondary)" }} />
                <Tooltip />
                <Bar dataKey="count" name="Ilość" fill="var(--fleet-purple)" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="fleet-chart-empty"><i className="pi pi-building" style={{ fontSize: "2rem", opacity: 0.3 }} /><p>Brak danych</p></div>
          )}
        </div>
      </div>

      {/* Bottom row — Recent repairs + Top cost vehicles */}
      {isAdmin && <div className="fleet-dashboard-bottom">
        {/* TOP 5 pojazdy wg kosztów napraw */}
        <div className="fleet-card">
          <div className="fleet-card-header">
            <h3 className="fleet-card-title"><i className="pi pi-sort-amount-down" /> Najdroższe pojazdy w naprawach</h3>
          </div>
          <div className="fleet-card-body--flush fleet-table">
            {summary?.topCostVehicles?.length > 0 ? (
              <DataTable value={summary.topCostVehicles} emptyMessage="Brak danych" size="small" responsiveLayout="stack" breakpoint="480px">
                <Column header="Pojazd" body={(row) => (
                  <span className="fleet-vehicle-link" onClick={() => navigate(`/pojazdy/${row.id}`)}>
                    {row.brand} {row.model}
                    <span className="fleet-mobile-sub">{row.registration_number}</span>
                  </span>
                )} />
                <Column header="Naprawy" body={(row) => row.repairCount} style={{ width: "80px" }} />
                <Column header="Łączny koszt" body={(row) => (
                  <span style={{ fontWeight: 600, color: "var(--fleet-danger)" }}>{formatCurrency(row.totalCost)}</span>
                )} style={{ width: "140px" }} />
              </DataTable>
            ) : (
              <div className="fleet-chart-empty"><p>Brak napraw</p></div>
            )}
          </div>
        </div>

        {/* Ostatnie naprawy */}
        <div className="fleet-card">
          <div className="fleet-card-header">
            <h3 className="fleet-card-title"><i className="pi pi-history" /> Ostatnie naprawy</h3>
          </div>
          <div className="fleet-card-body--flush fleet-table">
            {summary?.recentRepairs?.length > 0 ? (
              <DataTable value={summary.recentRepairs} emptyMessage="Brak danych" size="small" responsiveLayout="stack" breakpoint="480px">
                <Column header="Pojazd" body={(row) => (
                  <span style={{ fontWeight: 500 }}>{row.brand} {row.model}
                    <span className="fleet-mobile-sub">{row.registration_number}</span>
                  </span>
                )} />
                <Column header="Data" body={(row) => formatDate(row.repair_date)} style={{ width: "100px" }} />
                <Column header="Koszt" body={(row) => (
                  <span style={{ fontWeight: 600, color: "var(--fleet-text)" }}>{formatCurrency(row.cost)}</span>
                )} style={{ width: "110px" }} />
                <Column header="Kategoria" body={(row) => row.categoryName || "—"} />
              </DataTable>
            ) : (
              <div className="fleet-chart-empty"><p>Brak napraw</p></div>
            )}
          </div>
        </div>
      </div>}
    </div>
  );
};

export default VehicleDashboard;
